import {forwardRef, useEffect, useRef} from 'react';
import cx from 'classnames';

import {Alert} from '#component/base/Alert';
import {useFocusFirst} from '#hook';
import {sprinkles} from '#style/sprinkles';

import type {Props} from './Form.types';

/**
 * Form is a simple wrapper around an HTML `<form>` element, with a set of props
 * designed for use with "react-hook-form". More specifically:
 *
 * - The form component contains styles for arranging inputs vertically with a
 *   standard gap amount.
 * - The form component will redirect focus to the first focusable element in
 *   the Form after mounting.
 * - The form component will handle rendering form-level errors when a
 *   "react-hook-form" `errors` object is passed with a error key with the
 *   name "form".
 * - The form component will handle clearing "form" errors on submit events,
 *   when a "react-hook-form" `clearErrors` fn is passed.
 */
export const Form = forwardRef<HTMLFormElement, Props>(
  ({children, className, clearErrors, onSubmit, errors, autoFocus = false, ...otherProps}, ref) => {
    const formRef = useRef<HTMLFormElement>(null);
    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
      clearErrors?.('form');
      onSubmit?.(e);
    };

    useEffect(() => {
      // If a `ref` value has been forwarded, we need to share current
      // `formRef` value with it on every render.
      if (formRef && ref) {
        if (typeof ref === 'function') {
          ref(formRef.current);
        } else {
          // eslint-disable-next-line no-param-reassign
          ref.current = formRef.current;
        }
      }
    });

    useFocusFirst(formRef, [], autoFocus);

    return (
      <form
        className={cx(sprinkles({flex: 'flex', direction: 'col', gap: 64}), className)}
        onSubmit={handleSubmit}
        ref={formRef}
        {...otherProps}>
        {errors?.form && <Alert>{errors.form.message}</Alert>}

        {children}
      </form>
    );
  },
);
